import React from "react";

/**
 * Crea los componentes/serializadores para Portable Text usando tu cliente de Sanity.
 * Uso:
 *   <PortableText value={post.content} components={createPortableTextComponents(client)} />
 */
export function createPortableTextComponents(client) {
  // Build Sanity image URLs without relying on the `@sanity/image-url` package
  const urlFor = (source) => {
    const ref = source?.asset?._ref;
    if (!ref) return "";
    const [, id, , format] = ref.split("-");
    const projectId = client.config().projectId;
    const dataset = client.config().dataset;
    return `https://cdn.sanity.io/images/${projectId}/${dataset}/${id}.${format}?w=1200&fit=max&auto=format`;
  };

  return {
    block: {
      h1: ({ children }) => (
        <h1 className="text-3xl md:text-4xl font-semibold text-zinc-900 mt-8 mb-3">{children}</h1>
      ),
      h2: ({ children }) => (
        <h2 className="text-2xl md:text-3xl font-semibold text-zinc-900 mt-7 mb-3">{children}</h2>
      ),
      h3: ({ children }) => (
        <h3 className="text-xl md:text-2xl font-semibold text-zinc-900 mt-6 mb-2">{children}</h3>
      ),
      h4: ({ children }) => (
        <h4 className="text-lg md:text-xl font-semibold text-zinc-900 mt-5 mb-2">{children}</h4>
      ),
      normal: ({ children }) => <p className="leading-7 text-zinc-700 my-4">{children}</p>,
      blockquote: ({ children }) => (
        <blockquote className="border-l-4 border-zinc-300 pl-4 italic text-zinc-700 my-4">
          {children}
        </blockquote>
      ),
    },

    list: {
      bullet: ({ children }) => <ul className="list-disc pl-6 my-4 space-y-1">{children}</ul>,
      number: ({ children }) => <ol className="list-decimal pl-6 my-4 space-y-1">{children}</ol>,
    },

    listItem: {
      bullet: ({ children }) => <li className="text-zinc-700">{children}</li>,
      number: ({ children }) => <li className="text-zinc-700">{children}</li>,
    },

    marks: {
      strong: ({ children }) => <strong className="font-semibold text-zinc-900">{children}</strong>,
      em: ({ children }) => <em className="italic">{children}</em>,
      code: ({ children }) => (
        <code className="rounded bg-zinc-100 px-1 py-0.5 text-sm text-zinc-800">{children}</code>
      ),
      link: ({ children, value }) => {
        const href = value?.href || value?.url;
        const isInternal = href && (href.startsWith("/") || href.startsWith("#"));
        return (
          <a
            href={href}
            target={isInternal ? undefined : "_blank"}
            rel={isInternal ? undefined : "noopener noreferrer"}
            className="underline underline-offset-2 hover:text-zinc-900"
          >
            {children}
          </a>
        );
      },
    },

    types: {
      image: ({ value }) => {
        const alt = value?.alt || "";
        const src = urlFor(value);
        return (
          <figure className="my-6">
            <img src={src} alt={alt} className="rounded-lg border border-zinc-200 w-full" />
            {alt ? <figcaption className="mt-2 text-sm text-zinc-500">{alt}</figcaption> : null}
          </figure>
        );
      },
      code: ({ value }) => (
        <pre className="my-4 overflow-x-auto rounded-lg border border-zinc-200 p-4 text-sm leading-6">
          <code>{value?.code}</code>
        </pre>
      ),
    },

    // Fallbacks útiles por si aparece algo no mapeado
    unknownMark: ({ children }) => <span>{children}</span>,
    unknownType: () => null,
    unknownBlockStyle: ({ children }) => <p className="leading-7 text-zinc-700 my-4">{children}</p>,
  };
}